import React, { useState, useEffect } from 'react';
import { CloseButton } from '../common/CloseButton';
import './SongSettingsPanel.css';

/**
 * Props for SongSettingsPanel component
 * 
 * Requirements: 6.6
 */
export interface SongSettingsPanelProps {
  /** Current BPM value */
  bpm: number;
  /** Current steps value */
  steps: number;
  /** Callback when BPM button is tapped */
  onBPMTap: () => void;
  /** Callback when Steps button is tapped */
  onStepsTap: () => void;
  /** Callback when AI Generate button is tapped */
  onAIGenerate: () => void;
  /** Callback when Templates button is tapped */
  onTemplates: () => void;
  /** Callback when Save button is tapped */
  onSave: () => void;
  /** Callback when Share button is tapped */
  onShare: () => void;
  /** Callback when Export button is tapped */
  onExport: () => void;
  /** Callback when Reset button is tapped */
  onReset: () => void;
  /** Callback when panel is closed */
  onClose: () => void;
  /** Last saved timestamp (optional) */
  lastSavedAt?: number;
  /** Save indicator state - shows if currently saving */
  saveIndicator?: string;
}

/**
 * SongSettingsPanel Component
 * 
 * Full-screen panel for song-level settings and actions.
 * Provides access to BPM, Steps, Save, Share, Export, and Reset.
 * 
 * Requirements: 6.6
 */
export const SongSettingsPanel: React.FC<SongSettingsPanelProps> = ({
  bpm,
  steps,
  onBPMTap,
  onStepsTap,
  onAIGenerate,
  onTemplates,
  onSave,
  onShare,
  onExport,
  onReset,
  onClose,
  lastSavedAt,
  saveIndicator,
}) => {
  const [relativeTime, setRelativeTime] = useState<string>('');

  /**
   * Format timestamp as relative time (e.g., "2 seconds ago", "1 minute ago")
   */
  useEffect(() => {
    if (!lastSavedAt) {
      setRelativeTime('');
      return;
    }

    const updateRelativeTime = () => {
      const now = Date.now();
      const diff = now - lastSavedAt;
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);

      if (seconds < 10) {
        setRelativeTime('just now');
      } else if (seconds < 60) {
        setRelativeTime(`${seconds}s ago`);
      } else if (minutes < 60) {
        setRelativeTime(`${minutes}m ago`);
      } else if (hours < 24) {
        setRelativeTime(`${hours}h ago`);
      } else {
        const days = Math.floor(hours / 24);
        setRelativeTime(`${days}d ago`);
      }
    };

    // Update immediately
    updateRelativeTime();

    // Update every 10 seconds
    const interval = setInterval(updateRelativeTime, 10000);

    return () => clearInterval(interval);
  }, [lastSavedAt]);
  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="song-settings-panel-backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Panel container */}
      <div 
        className="song-settings-panel" 
        role="dialog" 
        aria-label="Song settings"
        aria-modal="true"
      >
        {/* Header with close button */}
        <div className="song-settings-panel-header">
          <div className="song-settings-panel-header-left">
            <h2 className="song-settings-panel-title">Song Settings</h2>
            {lastSavedAt && relativeTime && (
              <div className="song-settings-last-saved">
                <span 
                  className="song-settings-save-dot"
                  style={{
                    display: 'inline-block',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#00ff00',
                    boxShadow: saveIndicator ? '0 0 8px #00ff00' : 'none',
                    marginRight: '6px',
                    animation: saveIndicator ? 'pulse 0.6s ease-in-out' : 'none'
                  }}
                  aria-hidden="true"
                />
                <span className="song-settings-last-saved-text">
                  Saved {relativeTime}
                </span>
              </div>
            )}
          </div>
          <CloseButton
            onClick={onClose}
            ariaLabel="Close song settings"
            size="medium"
            variant="default"
          />
        </div>

        {/* Content area */}
        <div className="song-settings-panel-content">
          {/* AI & Templates Section */}
          <div className="song-settings-section">
            <h3 className="song-settings-section-title">
              <i className="fa-solid fa-wand-magic-sparkles"></i>
              Create
            </h3>
            <div className="song-settings-section-content">
              <button
                className="song-settings-button song-settings-button-primary"
                onClick={onAIGenerate}
                aria-label="Generate with AI"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-brain"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">AI Generate</span>
                  <span className="song-settings-button-description">Create music with AI</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button"
                onClick={onTemplates}
                aria-label="Browse templates"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-layer-group"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Templates</span>
                  <span className="song-settings-button-description">Start from a template</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>
            </div>
          </div>

          {/* Composition Settings Section */}
          <div className="song-settings-section">
            <h3 className="song-settings-section-title">
              <i className="fa-solid fa-sliders"></i>
              Composition Settings
            </h3>
            <div className="song-settings-section-content">
              <button
                className="song-settings-button"
                onClick={onBPMTap}
                aria-label={`Change BPM (currently ${bpm})`}
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-gauge-high"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Tempo (BPM)</span>
                  <span className="song-settings-button-value">{bpm}</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button"
                onClick={onStepsTap}
                aria-label={`Change steps (currently ${steps})`}
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-list-ol"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Pattern Length</span>
                  <span className="song-settings-button-value">{steps} steps</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>
            </div>
          </div>

          {/* Actions Section */}
          <div className="song-settings-section">
            <h3 className="song-settings-section-title">
              <i className="fa-solid fa-bolt"></i>
              Actions
            </h3>
            <div className="song-settings-section-content">
              <button
                className="song-settings-button"
                onClick={onSave}
                aria-label="Save composition"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-floppy-disk"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Save</span>
                  <span className="song-settings-button-description">Save to cloud</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button"
                onClick={onShare}
                aria-label="Share composition"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-share-nodes"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Share</span>
                  <span className="song-settings-button-description">Get shareable link</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button"
                onClick={onExport}
                aria-label="Export as WAV"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-download"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Export</span>
                  <span className="song-settings-button-description">Download as WAV</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button song-settings-button-danger"
                onClick={onReset}
                aria-label="Reset composition"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-rotate-left"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Reset</span>
                  <span className="song-settings-button-description">Clear everything</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
